import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactDOM from 'react-dom';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const QuotePopup = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    budget: '',
    projectDetails: '',
    timeline: ''
  });

  // Prevent body scroll when popup is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  // Validation function for each step
  const validateStep = (currentStep) => {
    const newErrors = {};
    
    if (currentStep === 1) {
      if (!formData.name.trim()) {
        newErrors.name = 'Name is required';
      }
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email';
      }
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required';
      } else if (!/^[+]?[\d\s-]{10,}$/.test(formData.phone.replace(/\s/g, ''))) {
        newErrors.phone = 'Please enter a valid phone number';
      }
    }
    
    if (currentStep === 2) {
      if (!formData.service) {
        newErrors.service = 'Please select a service';
      }
      if (!formData.projectDetails.trim()) {
        newErrors.projectDetails = 'Project details are required';
      } else if (formData.projectDetails.trim().length < 10) {
        newErrors.projectDetails = 'Please provide more details (at least 10 characters)';
      }
    }
    
    if (currentStep === 3) {
      if (!formData.budget) {
        newErrors.budget = 'Please select a budget range';
      }
      if (!formData.timeline) {
        newErrors.timeline = 'Please select a timeline';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    
    // Set colors
    const primaryColor = [15, 14, 151]; // rgb(15,14,151)
    const secondaryColor = [227, 41, 52]; // #e32934
    
    // Header with gradient effect
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 220, 40, 'F');
    
    // Company logo/name
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont(undefined, 'bold');
    doc.text('Alpha Laser Cutting', 20, 20);
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text('Precision Engineering Meets Advanced Technology', 20, 28);
    doc.text('India', 20, 33);
    
    // Document title
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text('QUOTE REQUEST', 20, 55);
    
    // Request date and ID
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    const today = new Date();
    const dateStr = today.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const quoteId = 'QR-' + Date.now().toString().slice(-8);
    doc.text(`Date: ${dateStr}`, 20, 63);
    doc.text(`Quote ID: ${quoteId}`, 20, 70);
    
    // Decorative line
    doc.setDrawColor(...secondaryColor);
    doc.setLineWidth(0.5);
    doc.line(20, 75, 190, 75);
    
    // Contact Information Section
    doc.setFillColor(240, 240, 240);
    doc.rect(15, 82, 180, 8, 'F');
    doc.setTextColor(...primaryColor);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('CONTACT INFORMATION', 20, 87);
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    
    let yPos = 97;
    doc.text('Full Name:', 20, yPos);
    doc.setFont(undefined, 'bold');
    doc.text(formData.name || 'N/A', 70, yPos);
    
    yPos += 7;
    doc.setFont(undefined, 'normal');
    doc.text('Email Address:', 20, yPos);
    doc.setFont(undefined, 'bold');
    doc.text(formData.email || 'N/A', 70, yPos);
    
    yPos += 7;
    doc.setFont(undefined, 'normal');
    doc.text('Phone Number:', 20, yPos);
    doc.setFont(undefined, 'bold');
    doc.text(formData.phone || 'N/A', 70, yPos);
    
    yPos += 7;
    doc.setFont(undefined, 'normal');
    doc.text('Company Name:', 20, yPos);
    doc.setFont(undefined, 'bold');
    doc.text(formData.company || 'N/A', 70, yPos);
    
    // Project Details Section
    yPos += 15;
    doc.setFillColor(240, 240, 240);
    doc.rect(15, yPos - 5, 180, 8, 'F');
    doc.setTextColor(...primaryColor);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('PROJECT DETAILS', 20, yPos);
    
    yPos += 10;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text('Service Required:', 20, yPos);
    doc.setFont(undefined, 'bold');
    doc.text(formData.service || 'N/A', 70, yPos);
    
    yPos += 10;
    doc.setFont(undefined, 'normal');
    doc.text('Project Description:', 20, yPos);
    
    // Handle multiline text for project details
    const projectText = formData.projectDetails || 'N/A';
    const splitText = doc.splitTextToSize(projectText, 120);
    doc.setFont(undefined, 'normal');
    doc.text(splitText, 20, yPos + 7);
    
    yPos += (splitText.length * 5) + 12;
    
    // Requirements Section
    doc.setFillColor(240, 240, 240);
    doc.rect(15, yPos - 5, 180, 8, 'F');
    doc.setTextColor(...primaryColor);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('PROJECT REQUIREMENTS', 20, yPos);
    
    yPos += 10;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text('Budget Range:', 20, yPos);
    doc.setFont(undefined, 'bold');
    doc.text(formData.budget || 'N/A', 70, yPos);
    
    yPos += 7;
    doc.setFont(undefined, 'normal');
    doc.text('Project Timeline:', 20, yPos);
    doc.setFont(undefined, 'bold');
    doc.text(formData.timeline || 'N/A', 70, yPos);
    
    // Footer
    yPos = 270;
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(0.5);
    doc.line(20, yPos, 190, yPos);
    
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text('Thank you for choosing Alpha Laser Cutting!', 105, yPos + 7, { align: 'center' });
    doc.text('We will review your request and get back to you within 24 hours.', 105, yPos + 12, { align: 'center' });
    doc.text('Contact: +91 93 63 63 88 11 / +91 93 63 63 88 22 | Email: alphalaserinfo@gmail.com', 105, yPos + 17, { align: 'center' });
    
    // Note box
    doc.setDrawColor(...secondaryColor);
    doc.setLineWidth(0.3);
    doc.rect(15, yPos + 23, 180, 15);
    doc.setFontSize(8);
    doc.setTextColor(...secondaryColor);
    doc.setFont(undefined, 'bold');
    doc.text('IMPORTANT NOTE:', 20, yPos + 28);
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'normal');
    doc.text('This is an automated quote request. Final pricing in INR will be provided after detailed assessment.', 20, yPos + 33);
    
    // Open PDF in new tab
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
    
    // Optional: Download automatically
    // doc.save(`Quote_Request_${quoteId}.pdf`);
  };

  const sendToWhatsApp = () => {
    const phoneNumber = '919363638811'; // WhatsApp number with country code
    
    // Format the message with all form details
    const message = `*New Quote Request - Alpha Laser Technologies*

*Contact Information:*
• Name: ${formData.name}
• Email: ${formData.email}
• Phone: ${formData.phone}
• Company: ${formData.company || 'N/A'}

*Project Details:*
• Service Required: ${formData.service}
• Project Description: ${formData.projectDetails}

*Requirements:*
• Budget Range: ${formData.budget}
• Timeline: ${formData.timeline}

---
_Sent from Alpha Laser Technologies Website_`;

    // Encode the message for URL
    const encodedMessage = encodeURIComponent(message);
    
    // Create WhatsApp URL
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    
    // Open WhatsApp in new tab
    window.open(whatsappUrl, '_blank');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate final step before submitting
    if (!validateStep(3)) {
      return;
    }
    
    console.log('Quote Request:', formData);
    
    // Send details to WhatsApp
    sendToWhatsApp();
    
    // Generate and open PDF
    generatePDF();
    
    // Close popup and reset form
    onClose();
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      service: '',
      budget: '',
      projectDetails: '',
      timeline: ''
    });
    setErrors({});
    setStep(1);
  };

  const nextStep = () => {
    if (validateStep(step)) {
      if (step < 3) setStep(step + 1);
    }
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const services = [
    'Laser Cutting',
    'Laser Engraving',
    'Metal Fabrication',
    'Custom Design',
    'Prototyping',
    'Industrial Solutions'
  ];

  const budgetRanges = [
    'Under ₹50,000',
    '₹50,000 - ₹2,00,000',
    '₹2,00,000 - ₹5,00,000',
    '₹5,00,000 - ₹10,00,000',
    '₹10,00,000+'
  ];

  const timelineOptions = [
    'ASAP',
    'Within 1 Week',
    '2-4 Weeks',
    '1-3 Months',
    '3+ Months'
  ];

  // Overlay variants
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.3 }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.3 }
    }
  };

  // Modal variants
  const modalVariants = {
    hidden: { 
      opacity: 0,
      scale: 0.8,
      rotateX: -15,
      y: 50
    },
    visible: { 
      opacity: 1,
      scale: 1,
      rotateX: 0,
      y: 0,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300,
        duration: 0.5
      }
    },
    exit: { 
      opacity: 0,
      scale: 0.8,
      rotateX: 15,
      y: -50,
      transition: {
        duration: 0.3
      }
    }
  };

  // Floating particles background
  const particles = Array.from({ length: 8 });

  // Portal element
  const [portalElement, setPortalElement] = useState(null);

  useEffect(() => {
    setPortalElement(document.body);
  }, []);

  if (!portalElement) return null;

  const popupContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-y-auto"
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {/* Backdrop with blur */}
          <motion.div
            className="absolute inset-0 bg-steel/70 backdrop-blur-sm z-0"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Subtle floating dots */}
          {particles.map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1.5 h-1.5 bg-white/20 rounded-full"
              initial={{
                x: Math.random() * window.innerWidth,
                y: Math.random() * window.innerHeight,
              }}
              animate={{
                y: [null, Math.random() * -80 - 30],
                opacity: [0, 0.5, 0],
              }}
              transition={{
                duration: Math.random() * 4 + 3,
                repeat: Infinity,
                delay: Math.random() * 2
              }}
            />
          ))}

          {/* Modal */}
          <motion.div
            className="relative w-full max-w-2xl max-h-[90vh] my-auto bg-white rounded-2xl shadow-2xl overflow-hidden z-10"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Top accent bar */}
            <div className="h-1.5 bg-primary w-full" />

            {/* Close button */}
            <motion.button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <svg className="w-4 h-4 text-steel" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </motion.button>

            {/* Content container with scroll */}
            <div className="relative overflow-y-auto max-h-[90vh] custom-scrollbar">
              <div className="p-6 md:p-8">
                {/* Header */}
                <motion.div
                  className="mb-6"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                      <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-xl md:text-2xl font-bold text-steel font-montserrat">
                        Request a Quote
                      </h2>
                      <p className="text-steel-lighter text-sm">
                        We'll respond within 24 hours
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Progress indicator */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    {[1, 2, 3].map((num) => (
                      <React.Fragment key={num}>
                        <motion.div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${
                            step >= num
                              ? 'bg-primary text-white'
                              : 'bg-gray-100 text-gray-400'
                          }`}
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.1 * num }}
                        >
                          {step > num ? (
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : num}
                        </motion.div>
                        {num < 3 && (
                          <div
                            className={`flex-1 h-0.5 rounded transition-all duration-500 ${
                              step > num ? 'bg-primary' : 'bg-gray-200'
                            }`}
                          />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                  <div className="flex justify-between text-xs text-steel-lighter">
                    <span>Contact</span>
                    <span>Project</span>
                    <span>Requirements</span>
                  </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit}>
                  <AnimatePresence mode="wait">
                    {/* Step 1: Contact Information */}
                    {step === 1 && (
                      <motion.div
                        key="step1"
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -30 }}
                        transition={{ duration: 0.25 }}
                        className="space-y-4"
                      >
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-steel mb-1.5">
                              Full Name <span className="text-secondary">*</span>
                            </label>
                            <input
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              placeholder="John Doe"
                              className={`w-full px-4 py-2.5 bg-gray-50 border rounded-lg focus:outline-none focus:bg-white transition-all duration-200 ${
                                errors.name 
                                  ? 'border-red-400 focus:border-red-500' 
                                  : 'border-gray-200 focus:border-primary'
                              }`}
                            />
                            {errors.name && (
                              <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-steel mb-1.5">
                              Email <span className="text-secondary">*</span>
                            </label>
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              placeholder="john@example.com"
                              className={`w-full px-4 py-2.5 bg-gray-50 border rounded-lg focus:outline-none focus:bg-white transition-all duration-200 ${
                                errors.email 
                                  ? 'border-red-400 focus:border-red-500' 
                                  : 'border-gray-200 focus:border-primary'
                              }`}
                            />
                            {errors.email && (
                              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                            )}
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-steel mb-1.5">
                              Phone <span className="text-secondary">*</span>
                            </label>
                            <input
                              type="tel"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              placeholder="+91 93 63 63 88 11"
                              className={`w-full px-4 py-2.5 bg-gray-50 border rounded-lg focus:outline-none focus:bg-white transition-all duration-200 ${
                                errors.phone 
                                  ? 'border-red-400 focus:border-red-500' 
                                  : 'border-gray-200 focus:border-primary'
                              }`}
                            />
                            {errors.phone && (
                              <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-steel mb-1.5">
                              Company
                            </label>
                            <input
                              type="text"
                              name="company"
                              value={formData.company}
                              onChange={handleChange}
                              placeholder="Your Company"
                              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:bg-white focus:border-primary transition-all duration-200"
                            />
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Step 2: Project Details */}
                    {step === 2 && (
                      <motion.div
                        key="step2"
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -30 }}
                        transition={{ duration: 0.25 }}
                        className="space-y-4"
                      >
                        <div>
                          <label className="block text-sm font-medium text-steel mb-1.5">
                            Service Required <span className="text-secondary">*</span>
                          </label>
                          <select
                            name="service"
                            value={formData.service}
                            onChange={handleChange}
                            className={`w-full px-4 py-2.5 bg-gray-50 border rounded-lg focus:outline-none focus:bg-white transition-all duration-200 cursor-pointer ${
                              errors.service 
                                ? 'border-red-400 focus:border-red-500' 
                                : 'border-gray-200 focus:border-primary'
                            }`}
                          >
                            <option value="">Select a service</option>
                            {services.map((service) => (
                              <option key={service} value={service}>
                                {service}
                              </option>
                            ))}
                          </select>
                          {errors.service && (
                            <p className="text-red-500 text-xs mt-1">{errors.service}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-steel mb-1.5">
                            Project Details <span className="text-secondary">*</span>
                          </label>
                          <textarea
                            name="projectDetails"
                            value={formData.projectDetails}
                            onChange={handleChange}
                            placeholder="Tell us about your project..."
                            rows="5"
                            className={`w-full px-4 py-2.5 bg-gray-50 border rounded-lg focus:outline-none focus:bg-white transition-all duration-200 resize-none ${
                              errors.projectDetails 
                                ? 'border-red-400 focus:border-red-500' 
                                : 'border-gray-200 focus:border-primary'
                            }`}
                          />
                          {errors.projectDetails && (
                            <p className="text-red-500 text-xs mt-1">{errors.projectDetails}</p>
                          )}
                        </div>
                      </motion.div>
                    )}

                    {/* Step 3: Requirements */}
                    {step === 3 && (
                      <motion.div
                        key="step3"
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -30 }}
                        transition={{ duration: 0.25 }}
                        className="space-y-4"
                      >
                        <div>
                          <label className="block text-sm font-medium text-steel mb-1.5">
                            Budget Range <span className="text-secondary">*</span>
                          </label>
                          <select
                            name="budget"
                            value={formData.budget}
                            onChange={handleChange}
                            className={`w-full px-4 py-2.5 bg-gray-50 border rounded-lg focus:outline-none focus:bg-white transition-all duration-200 cursor-pointer ${
                              errors.budget 
                                ? 'border-red-400 focus:border-red-500' 
                                : 'border-gray-200 focus:border-primary'
                            }`}
                          >
                            <option value="">Select your budget</option>
                            {budgetRanges.map((range) => (
                              <option key={range} value={range}>
                                {range}
                              </option>
                            ))}
                          </select>
                          {errors.budget && (
                            <p className="text-red-500 text-xs mt-1">{errors.budget}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-steel mb-1.5">
                            Timeline <span className="text-secondary">*</span>
                          </label>
                          <select
                            name="timeline"
                            value={formData.timeline}
                            onChange={handleChange}
                            className={`w-full px-4 py-2.5 bg-gray-50 border rounded-lg focus:outline-none focus:bg-white transition-all duration-200 cursor-pointer ${
                              errors.timeline 
                                ? 'border-red-400 focus:border-red-500' 
                                : 'border-gray-200 focus:border-primary'
                            }`}
                          >
                            <option value="">When do you need this?</option>
                            {timelineOptions.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                          {errors.timeline && (
                            <p className="text-red-500 text-xs mt-1">{errors.timeline}</p>
                          )}
                        </div>

                        <div className="bg-primary/5 rounded-lg p-4 border border-primary/10">
                          <div className="flex items-start gap-3">
                            <svg className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                              <h4 className="font-medium text-steel text-sm mb-1">What's Next?</h4>
                              <p className="text-xs text-steel-lighter">
                                Our team will review your request and contact you within 24 hours with a detailed quote.
                              </p>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Navigation buttons */}
                  <div className="flex justify-between items-center mt-6 pt-5 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={prevStep}
                      disabled={step === 1}
                      className={`px-5 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                        step === 1
                          ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                          : 'bg-gray-100 text-steel hover:bg-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-1.5">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        <span>Back</span>
                      </div>
                    </button>

                    {step < 3 ? (
                      <button
                        type="button"
                        onClick={nextStep}
                        className="px-6 py-2.5 bg-primary text-white rounded-lg font-medium text-sm hover:bg-primary/90 transition-all duration-200"
                      >
                        <div className="flex items-center gap-1.5">
                          <span>Continue</span>
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </button>
                    ) : (
                      <motion.button
                        type="submit"
                        className="px-6 py-2.5 bg-primary text-white rounded-lg font-medium text-sm hover:bg-primary/90 transition-all duration-200"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="flex items-center gap-1.5">
                          <span>Submit Request</span>
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      </motion.button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </motion.div>
        </motion.div>
        )}
      </AnimatePresence>
    );

  return ReactDOM.createPortal(popupContent, portalElement);
};

export default QuotePopup;